import React from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '@/shared/hooks/usePageTitle';
import PolicyPage, { SupportAddress } from './PolicyPage';

/**
 * The rules for using the exchange.
 *
 * The partners are public institutions, so this page deliberately has no
 * arbitration clause and no waiver of governmental immunity: a Colorado
 * public body cannot agree to either through a click-through page, and the
 * clause that preserves the Governmental Immunity Act is the one their
 * counsel will look for first.
 */
const TITLE = 'Terms of use';

function TermsPage() {
    usePageTitle(TITLE);
    return (
        <PolicyPage
            title={TITLE}
            summary={
                <>
                    The exchange lets campus departments give surplus furniture
                    and equipment to one another at no cost. Using it means
                    agreeing to these terms and to the{' '}
                    <Link to="/privacy">privacy statement</Link>.
                </>
            }
        >
            <h2>The service</h2>
            <p>
                The Auraria Furniture Exchange is a pilot for the Auraria
                Sustainable Campus Program partners: Metropolitan State
                University of Denver, the University of Colorado Denver, the
                Community College of Denver, and the Auraria Higher Education
                Center. It is built and hosted by the Community-Centered
                Computing (C3) Lab at MSU Denver. Every transfer is free and
                between departments. The exchange is not a store and never
                handles money.
            </p>

            <h2>Who may use it</h2>
            <p>
                Faculty and staff of the four partner institutions, signing up
                with their campus email address, and acting for their
                department. Keep your password to yourself, use one account, and
                keep your name and department accurate. You are responsible for
                what is done with your account.
            </p>

            <h2>What you may list</h2>
            <p>
                Furniture and equipment owned by your institution that your
                department is authorized to give away. Your institution&rsquo;s
                property and surplus rules still apply to every transfer:
            </p>
            <ul>
                <li>
                    Capital equipment still needs your institution&rsquo;s
                    disposal or transfer form. The exchange links to that
                    process; it does not replace it.
                </li>
                <li>
                    Computers and anything else that stores data must be cleared
                    under your institution&rsquo;s IT policy before they are
                    listed.
                </li>
                <li>
                    Follow your building&rsquo;s facilities rules for moving
                    items. Shared Auraria buildings route moves through AHEC
                    Facilities.
                </li>
            </ul>
            <p>
                The <Link to="/guidelines">reuse guidelines</Link> carry each
                institution&rsquo;s specific rules.
            </p>

            <h2>What is not allowed</h2>
            <ul>
                <li>Selling, pricing, or asking for payment or trades.</li>
                <li>
                    Personal property, or giving items to anyone for personal
                    use.
                </li>
                <li>
                    Hazardous materials, chemicals, biological materials,
                    weapons, or recalled products.
                </li>
                <li>
                    Student records or other confidential information in a
                    listing, photograph, or message.
                </li>
                <li>
                    Misleading listings, harassment, or messages unrelated to a
                    transfer.
                </li>
                <li>
                    Automated collection of data from the site, or any attempt
                    to get around its access controls.
                </li>
            </ul>

            <h2>Transfers</h2>
            <p>
                Departments arrange each transfer between themselves. The
                exchange does not inspect items and makes no promise about their
                condition, safety, or fitness for a purpose. Check an item
                before you accept it.
            </p>

            <h2>Your content</h2>
            <p>
                You are responsible for what you post. By posting, you allow the
                exchange to show your listing, its photographs, and your contact
                details to other members for as long as the listing is on the
                site. Coordinators named by the partner institutions may remove
                listings and deactivate accounts that break these terms.
            </p>

            <h2>Email</h2>
            <p>
                The exchange emails you about your own listings and requests. It
                also sends a weekly digest of new items, which you can turn off
                on your profile.
            </p>

            <h2>Pilot status</h2>
            <p>
                The exchange is provided as it is, while it is being tested.
                Features may change, the service may be interrupted, and the
                pilot may end. We will tell members before the pilot ends.
            </p>

            <h2>Liability</h2>
            <p>
                To the extent Colorado law permits, the partner institutions and
                the C3 Lab are not liable for loss or damage arising from use of
                the exchange or from a transfer arranged through it. Nothing in
                these terms waives any immunity, protection, or limitation
                available to a partner institution or its employees under the
                Colorado Governmental Immunity Act, C.R.S. 24-10-101 and
                following, or any other law.
            </p>

            <h2>Governing law</h2>
            <p>
                These terms are governed by the laws of the State of Colorado.
            </p>

            <h2>Accessibility</h2>
            <p>
                The exchange is built to meet WCAG 2.1 Level AA. The{' '}
                <Link to="/accessibility">accessibility statement</Link> says
                how it is tested and how to report a barrier.
            </p>

            <h2>Changes to these terms</h2>
            <p>
                When these terms change, we update the date at the top of the
                page. For a significant change, we will also tell members by
                email.
            </p>

            <h2>Contact</h2>
            <p>
                Questions about these terms go to <SupportAddress />.
            </p>
        </PolicyPage>
    );
}

export default TermsPage;
